import { Button, Theme } from "@astryxdesign/core";
import { useEffect, useRef, useState } from "react";
import { openPilot } from "@/lib/pilot";
import { hireosTheme } from "@/theme/hireos";

const SIGN_IN = "https://admin.hireos.co.nz";

export function Mark() {
  return (
    <a className="mark" href="/">
      <i />
      HireOS
    </a>
  );
}

/**
 * The nav, and the sheet its hamburger opens.
 *
 * Clear over the hero's film, frosted dark over it once the page scrolls, and
 * the page's paper past it; a page with no hero gets the paper throughout,
 * bar the landing and integrations pages, whose bar is the top's lime until it
 * reaches the white.
 * The bar is the mark and
 * the two actions; every page link is in the sheet the hamburger opens, at
 * every width. On a phone Sign in leaves the bar and becomes the sheet's last
 * row; Try for free stays in the bar, so the sheet does not repeat it.
 */
export function Nav() {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const bar = useRef<HTMLElement>(null);
  const [overHero, setOverHero] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lime, setLime] = useState(false);

  // Over the hero while any of it is still under the bar.
  useEffect(() => {
    const hero = document.querySelector(".hero");
    const nav = bar.current;
    if (!hero || !nav) return;
    const io = new IntersectionObserver(([e]) => setOverHero(!!e?.isIntersecting), {
      rootMargin: `-${nav.offsetHeight}px 0px 0px 0px`,
    });
    io.observe(hero);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const intro = document.querySelector<HTMLElement>(".intro, .ig-head");
    const onScroll = () => {
      setScrolled(window.scrollY > 0);
      // Lime while the bar's foot is still above where the intro's lime stops.
      const nav = bar.current;
      if (!intro || !nav) return;
      const stop = parseFloat(getComputedStyle(intro).getPropertyValue("--lime-stop")) || 0;
      setLime(intro.getBoundingClientRect().bottom - stop > nav.getBoundingClientRect().bottom);
    };
    onScroll();
    addEventListener("scroll", onScroll, { passive: true });
    addEventListener("resize", onScroll);
    return () => {
      removeEventListener("scroll", onScroll);
      removeEventListener("resize", onScroll);
    };
  }, []);

  // The page stays put under the menu: a wheel or a drag does nothing unless
  // the menu itself has more to scroll.
  useEffect(() => {
    document.body.classList.toggle("menu-open", open);
    if (!open) return;

    const hold = (e: Event) => {
      const el = sheet.current;
      const target = e.target as Node | null;
      if (el && target && el.contains(target) && el.scrollHeight > el.clientHeight) return;
      e.preventDefault();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };

    addEventListener("wheel", hold, { passive: false });
    addEventListener("touchmove", hold, { passive: false });
    document.addEventListener("keydown", key);
    return () => {
      removeEventListener("wheel", hold);
      removeEventListener("touchmove", hold);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  useEffect(() => () => document.body.classList.remove("menu-open"), []);

  return (
    <>
      <nav
        ref={bar}
        className={open ? "open" : [overHero && "over", scrolled && "scrolled", lime && "lime"].filter(Boolean).join(" ") || undefined}
      >
        <div className="in">
          <Mark />
          <div className="right">
            {/* Over the film the buttons take the theme's dark mode. */}
            <Theme theme={hireosTheme} mode={overHero && !open ? "dark" : "light"}>
              <Button label="Sign in" variant="ghost" href={SIGN_IN} className="signin" />
              <Button label="Try for free" variant="ghost" onClick={openPilot} />
            </Theme>
            <button
              className="menu"
              ref={button}
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
              aria-controls="menu"
              onClick={() => setOpen((v) => !v)}
            >
              <svg className="bars" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M4 7h16M4 12h16M4 17h16" />
              </svg>
              <svg className="x" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      <div className="sheet" id="menu" ref={sheet} hidden={!open}>
        <a href="/#faq" onClick={() => setOpen(false)}>
          FAQ
        </a>
        <a href="/integrations" onClick={() => setOpen(false)}>
          Integrations
        </a>
        <a href="/about" onClick={() => setOpen(false)}>
          About
        </a>
        <a href="/terms" onClick={() => setOpen(false)}>
          Terms
        </a>
        <a href="/privacy" onClick={() => setOpen(false)}>
          Privacy
        </a>
        {/* Only where the bar has dropped its Sign in button */}
        <a className="signin" href={SIGN_IN} onClick={() => setOpen(false)}>
          Sign in
        </a>
      </div>
    </>
  );
}
